import {
  Button,
  ButtonGroup,
  DataTable,
  DataTableWC,
  Icon,
  Image,
  PageBody,
  PageSection,
  Result,
  Search,
  Spacer,
  Stack,
  Text,
  TextTitle,
  TextCaption,
} from '@pypestream/design-system';
import {
  dateFnsLocales,
  fallbackLanguage,
  TranslationComponent,
  useTranslation,
} from '@pypestream/translations';
import { formatTimeWithTimezone } from '@pypestream/utils';
import { html } from 'lit/static-html.js';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { DEFAULT_SQUARE_ICON_BASE64, Loader } from '../../../components';
import {
  OrganizationRoleNames,
  useAuthRole,
  useFormatTimeWithTimezone,
  useUserTimeZone,
} from '../../../hooks';
import {
  sendManagerEvent,
  useManagerCtxSelector,
  useManagerStateMatches,
  useManagerStateMatchesAllOf,
} from '../../../xstate/app.xstate';
import { IPage } from '../../types';
import {
  nextRetryPhase,
  resolveChildOrgsAnnouncement,
  resolveChildOrgsPanels,
  resolveChildOrgsView,
  type RetryPhase,
} from './child-orgs-view';
import { debounce } from 'lodash';
import { SEARCH_DEBOUNCE_TIME } from '../../../utils/constants';

export const Organizations: React.FC<IPage> = ({ title }) => {
  const loaded = useManagerStateMatches('orgRelated.ready.childOrgs.loaded');
  const failed = useManagerStateMatches('orgRelated.ready.childOrgs.error');
  const view = resolveChildOrgsView({ loaded, failed });
  const isReadyToLoad = useManagerStateMatchesAllOf([
    'orgRelated.ready.childOrgs.idle',
    'orgRelated.ready.currentOrg.selected',
  ]);

  const { childOrgs, defaultLanguage, organizationId } = useManagerCtxSelector(
    (ctx) => ({
      childOrgs: ctx.childOrgs,
      defaultLanguage: ctx.userInfo?.defaultLanguage,
      organizationId: ctx.selectedOrgId,
    })
  );

  const isAdminRole = useAuthRole([
    OrganizationRoleNames.ADMIN,
    OrganizationRoleNames.SUPER_ADMIN,
  ]);

  const navigate = useNavigate();

  const tableRef = useRef<DataTableWC>(null);
  const [t] = useTranslation();
  const userTimeZone = useUserTimeZone();

  const currentOrgCreationDate = useFormatTimeWithTimezone({
    fromDate: childOrgs.currentOrg?.createdAt,
  });

  useEffect(() => {
    if (isReadyToLoad) {
      sendManagerEvent('manager.childOrgs.loadChildOrgs');
    }
  }, [isReadyToLoad]);

  // PSTUDIO-4177 (WCAG 2.4.3). A retry moves the machine error -> loading, and
  // if the error panel were simply swapped for the loader the button the user
  // just pressed would unmount under their focus, dropping it to <body>. Keeping
  // the panel mounted for the duration of the retry — with the button in its
  // busy state — leaves focus where it was.
  //
  // The phase is written by the CLICK, never derived from a rendered frame: a
  // retry whose `loading` frame React batches away still has to announce itself.
  const [retryPhase, setRetryPhase] = useState<RetryPhase>('idle');

  const retryLoadChildOrgs = () => {
    setRetryPhase('requested');
    sendManagerEvent('manager.childOrgs.loadChildOrgs');
  };

  useEffect(() => {
    setRetryPhase((phase) => nextRetryPhase({ view, phase }));
  }, [view]);

  const { showLoader, showError, busyRetry } = resolveChildOrgsPanels({
    view,
    retrying: retryPhase === 'requested',
  });

  const loadErrorTitle =
    t('manager/organizations:childOrgsList.loadError.title', {
      defaultValue: "We couldn't load the child organizations",
    }) || '';

  const announcementKey = resolveChildOrgsAnnouncement({
    view,
    busyRetry,
    recovered: retryPhase === 'recovered',
  });

  // A switch, not a map of all four: this renders on every keystroke of the
  // search box below, and three of the four strings are discarded each time.
  const announcement = (() => {
    switch (announcementKey) {
      case 'error':
        return loadErrorTitle;
      case 'retrying':
        return (
          t('manager/organizations:childOrgsList.loadError.retrying', {
            defaultValue: 'Retrying…',
          }) || ''
        );
      case 'recovered':
        return (
          t('manager/organizations:childOrgsList.loadError.recovered', {
            defaultValue: 'Child organizations loaded',
          }) || ''
        );
      default:
        return '';
    }
  })();

  // PSTUDIO-4177 (WCAG 2.4.3), the other half of keeping focus: a SUCCESSFUL
  // retry unmounts the panel the button lived in, so focus falls to <body> and
  // the next Tab restarts from the top of the document. Move it to the section
  // the user was waiting for — but only if it really was dropped, so this can
  // never steal focus from somewhere the user has since moved to.
  const recoveredSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (retryPhase !== 'recovered' || view !== 'ready') return;

    if (document.activeElement === document.body) {
      recoveredSectionRef.current?.focus();
    }
  }, [retryPhase, view]);

  return (
    <>
      <PageBody background="none" width="large">
        <>
          {/*
            WCAG 4.1.3. The panel below replaces the loader without moving
            focus, and `ps-result` renders a plain container — no role, no
            aria-live — so its arrival is silent to a screen reader. This region
            is mounted for every view and only its text changes, because a live
            region inserted together with its content is not announced by every
            assistive technology.

            LIMITATION: that only holds while the page stays mounted. Arriving
            on a route whose machine is ALREADY parked in `error` puts the
            region and its text into the DOM in the same commit, and the
            announcement may be missed. The machine also raises a toast on the
            failure (`onErrorActions` -> `sendUserMessage`), which react-toastify
            renders with `role="alert"` — so a failure is announced twice, once
            assertively with the raw error text and once politely here. That is
            pre-existing and deliberately not changed under this ticket.

            The literals are the standard clip-rect idiom, not design values.
          */}
          <div
            role="status"
            aria-live="polite"
            data-testid="child-orgs-live-region"
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
            }}
          >
            {announcement}
          </div>
          {showLoader && (
            <PageSection>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Loader relative />
              </div>
            </PageSection>
          )}
          {showError && (
            <PageSection>
              <Result
                align="center"
                status="error"
                test-id="child-orgs-load-error"
                label={loadErrorTitle}
                body={
                  t('manager/organizations:childOrgsList.loadError.body', {
                    defaultValue:
                      'Something went wrong while loading this list.',
                  }) || ''
                }
              >
                <Stack gutter="xsmall" justifyContent="center" slot="footer">
                  <Button
                    size="large"
                    test-id="child-orgs-retry-btn"
                    loading={busyRetry}
                    onClick={retryLoadChildOrgs}
                  >
                    <TranslationComponent i18nKey="manager/organizations:childOrgsList.loadError.retry">
                      Try again
                    </TranslationComponent>
                  </Button>
                </Stack>
              </Result>
            </PageSection>
          )}
          {view === 'ready' && (
            /*
              A plain div, not the Stack below: the design system's React
              wrappers type `ref` against their PROPS object rather than the
              element (`GeneratePropsWithRef`), and they do not accept
              `tabIndex` at all, so a focus target has to be a native node.
              It wraps the section rather than preceding it, so focus lands on
              something that has an accessible name to read.
            */
            <div ref={recoveredSectionRef} tabIndex={-1}>
              <Stack alignItems="center" justifyContent="space-between">
                <Stack
                  gutter="small"
                  justifyContent="start"
                  alignItems="center"
                  direction="row"
                  nowrap
                >
                  <Image
                    width="80px"
                    height="80px"
                    alt={childOrgs.currentOrg?.name || 'Organization Logo'}
                    src={
                      childOrgs.currentOrg?.pictureFile?.url ||
                      DEFAULT_SQUARE_ICON_BASE64
                    }
                    fallback={DEFAULT_SQUARE_ICON_BASE64}
                  />
                  <Stack gutter="none" display="inline-flex" direction="column">
                    <TextTitle test-id="org-name">
                      {childOrgs.currentOrg?.name}
                    </TextTitle>
                    <TextCaption>
                      {t('manager/organizations:childOrgsList.created', {
                        date: currentOrgCreationDate,
                      })}
                    </TextCaption>
                  </Stack>
                </Stack>
                {childOrgs.currentOrg && isAdminRole && (
                  <ButtonGroup>
                    <Button
                      test-id="edit-org-button"
                      size="large"
                      variant="secondary"
                      onClick={() =>
                        navigate(
                          `/organization/${childOrgs.currentOrg?.id}/edit`
                        )
                      }
                    >
                      <Icon slot="prefix" name="edit" size="xsmall" />
                      <TranslationComponent i18nKey="manager/organizations:childOrgsList.editOrg">
                        Edit Org
                      </TranslationComponent>
                    </Button>
                  </ButtonGroup>
                )}
              </Stack>
              <Spacer size="large" />
              <PageSection size="small">
                <Stack alignItems="center" justifyContent="end">
                  <TextTitle size="xsmall" style={{ marginInlineEnd: 'auto' }}>
                    {t('manager/organizations:childOrgsList.childOrgs', {
                      count: childOrgs.count,
                      defaultValue: 'Child Organizations ({{count}})',
                    })}
                  </TextTitle>
                  <Search
                    size="large"
                    style={{ maxWidth: '240px' }}
                    onInput={debounce((e) => {
                      tableRef.current?.table.setGlobalFilter(
                        String(e.target?.value) || ''
                      );
                    }, SEARCH_DEBOUNCE_TIME)}
                  ></Search>
                  {isAdminRole && (
                    <ButtonGroup>
                      <Button
                        test-id="create-child-org-btn"
                        size="large"
                        onClick={() =>
                          navigate(
                            `/organization/${organizationId}/child-orgs/create`
                          )
                        }
                      >
                        <Icon slot="prefix" name="add" size="xsmall" />
                        <TranslationComponent i18nKey="manager/organizations:childOrgsList.createOrg">
                          Create Child Org
                        </TranslationComponent>
                      </Button>
                    </ButtonGroup>
                  )}
                </Stack>
                <Spacer size="large"></Spacer>
                <DataTable
                  test-id="child-orgs-table"
                  size="small"
                  ref={tableRef}
                  filterColumns={['name', 'teams', 'projects', 'organizations']}
                  columns={[
                    {
                      accessorKey: 'name',
                      header: () =>
                        t('manager/organizations:childOrgsList.table.name', {
                          defaultValue: 'Name',
                        }) || '',
                      cell: ({ row, renderValue }) => html`
                        <ps-stack
                          gutter="small"
                          justifyContent="start"
                          alignItems="center"
                          direction="row"
                          nowrap
                        >
                          <ps-avatar
                            style="flex-shrink: 0"
                            size="medium"
                            src="${row.original.pictureFile?.url ||
                            DEFAULT_SQUARE_ICON_BASE64}"
                            fallback=${DEFAULT_SQUARE_ICON_BASE64}
                            alt=${childOrgs.currentOrg?.name ||
                            'Organization Logo'}
                          ></ps-avatar>

                          <ps-text-subtitle
                            size="small"
                            truncate
                            style="min-width: 0px"
                            >${row.original.name}</ps-text-subtitle
                          >
                        </ps-stack>
                      `,
                    },
                    {
                      accessorKey: 'users',
                      header: () =>
                        t('manager/organizations:childOrgsList.table.users', {
                          defaultValue: 'Users',
                        }) || '',
                      cell: ({ row, renderValue }) =>
                        html`<ps-text-body variant="secondary"
                          >${row.original.users?.count || '0'}</ps-text-body
                        >`,
                    },
                    {
                      accessorKey: 'teams',
                      accessorFn: (row) =>
                        row.teams?.rows?.map((team) => team.name).join(', '),
                      header: () =>
                        t('manager/organizations:childOrgsList.table.teams', {
                          defaultValue: 'Teams',
                        }) || '',
                      cell: ({ row, renderValue }) =>
                        html`<ps-text-body variant="secondary"
                          >${row.original.teams?.rows
                            ?.map?.((team) => team.name)
                            .join(', ')}</ps-text-body
                        >`,
                    },
                    {
                      accessorKey: 'projects',
                      accessorFn: (row) =>
                        row.projects?.rows
                          ?.map((project) => project.name)
                          .join(', '),
                      header: () =>
                        t(
                          'manager/organizations:childOrgsList.table.projects',
                          {
                            defaultValue: 'Projects',
                          }
                        ) || '',
                      cell: ({ row, renderValue }) =>
                        html`<ps-text-body variant="secondary"
                          >${row.original.projects?.rows
                            ?.map?.((project) => project.name)
                            .join(', ')}</ps-text-body
                        >`,
                    },
                    {
                      accessorKey: 'organizations',
                      accessorFn: (row) =>
                        row.allChildAccounts?.map((org) => org.name).join(', '),
                      header: () =>
                        t(
                          'manager/organizations:childOrgsList.table.childOrgs',
                          {
                            defaultValue: 'Child Orgs',
                          }
                        ) || '',
                      cell: ({ row, renderValue }) =>
                        html`<ps-text-body variant="secondary"
                          >${row.original.allChildAccounts
                            ?.map?.((org) => org.name)
                            .join(', ')}</ps-text-body
                        >`,
                    },
                    {
                      accessorKey: 'updated',
                      accessorFn: (row) => row.updatedAt?.toString(),
                      header: () =>
                        t('manager/organizations:childOrgsList.table.updated', {
                          defaultValue: 'Last updated',
                        }) || '',
                      cell: ({ row, renderValue }) =>
                        html`<div style="min-width: 90px">
                          <ps-text-body
                            variant="secondary"
                            textAlign="end"
                            nowrap
                          >
                            ${formatTimeWithTimezone({
                              fromDate: row.original.updatedAt,
                              locale:
                                dateFnsLocales[
                                  defaultLanguage || fallbackLanguage
                                ],
                              userTimeZone,
                            })}</ps-text-body
                          >
                        </div>`,
                      meta: {
                        align: 'end',
                      },
                    },
                  ]}
                  data={childOrgs.rows}
                  onRowSelectionChange={(event) => {
                    if (event.detail.selected && event.detail.selected.length) {
                      event.detail.selected.map((item) => {
                        const dataTable = event.currentTarget as DataTableWC;

                        dataTable['table'].resetRowSelection();

                        sendManagerEvent({
                          type: 'manager.selectChildOrg',
                          id: item.original.id,
                        });

                        navigate(
                          `/organization/${item.original.id}/child-orgs`
                        );

                        return item;
                      });
                    }
                  }}
                  enableRowSelection
                ></DataTable>

                {!childOrgs ||
                  (!childOrgs.rows?.length && (
                    <Text
                      size="xsmall"
                      textAlign="center"
                      className="u-margin-top-xlarge"
                    >
                      <TranslationComponent i18nKey="manager/organizations:childOrgsList.createToGetStarted">
                        Create an org to get started.
                      </TranslationComponent>
                    </Text>
                  ))}
              </PageSection>
            </div>
          )}
        </>
      </PageBody>
    </>
  );
};
